import { sdkRequest } from '@eventbrite/legacy-infra.http';
import { type EventInteractionWithContext, EventInteractions } from '../types';
import { BaseInteractionsLogger } from './BaseInteractionsLogger';

/**
 * Data structure expected by the /api/v3/log_engagement/ endpoint
 */
type InteractionEngagementLoggerData = {
  identity: {
    user_id?: string | number;
    locale?: string;
    tracking_code: null;
    device_latitude: null;
    device_longitude: null;
  };
  interface_context: {
    app: string;
    interface: string;
    placement_id?: string;
    rank: number;
    absolute_rank?: number;
    event_id?: string;
    ad_id?: string;
    action?: string;
    tab_key?: string;
    campaign_id: null;
    page: number;
    sub_interface: string;
    position: string;
  };
  retrieval_context?: Record<string, unknown>;
  display_context?: {
    has_open_promotion: boolean;
    has_bogo_label: boolean;
    urgency_signals: string[];
  };
  venue_context?: {
    venue_id: string | null;
  };
  feedback_context?: {
    email?: string;
    report_reason?: string;
    report_description?: string;
  };
};

export class InteractionsEngagementLogger extends BaseInteractionsLogger<InteractionEngagementLoggerData> {
  protected excludedEvents: EventInteractions[] = [EventInteractions.Mounted];

  /**
   * Logs the transformed interactions to the engagement API
   *
   * @param transformedInteractions - Array of interactions in API format
   */
  protected log(transformedInteractions: InteractionEngagementLoggerData[]) {
    this.logToEngagementAPI(transformedInteractions);
  }

  /**
   * Sends the transformed interactions to the /api/v3/log_engagement/ API
   *
   * @param interactions - Array of interactions in the API format
   */
  private logToEngagementAPI(interactions: InteractionEngagementLoggerData[]) {
    sdkRequest(
      '/log_engagement/',
      {
        method: 'POST',
        body: JSON.stringify(interactions),
        headers: { 'EB-version': 'custom-events-next-version' },
      },
      process.env.NEXT_PUBLIC_PROXY_API_V3 ? '/e' : '',
    );
  }

  /**
   * Transforms an event interaction into the format expected by the API
   *
   * @param interaction - The event interaction to transform
   * @returns The interaction in API format
   */
  protected transformInteraction(interaction: EventInteractionWithContext): InteractionEngagementLoggerData {
    return {
      identity: {
        user_id: interaction.user.id ?? 0,
        locale: interaction.user.locale,
        tracking_code: null,
        device_latitude: null,
        device_longitude: null,
      },
      interface_context: {
        app: 'web',
        interface: interaction.place.name,
        placement_id: interaction.place.placementId,
        rank: interaction.place.position,
        absolute_rank: interaction.place.absoluteRank,
        event_id: interaction.id,
        ad_id: interaction.adId,
        action: interaction.action,
        tab_key: interaction.place.tabKey,
        campaign_id: null,
        page: interaction.place.page,
        // log_engagement endpoint breaks if sub_interface / position are not sent
        sub_interface: interaction.place.subInterface?.name ?? '',
        position: interaction.place.subInterface?.position ?? '',
      },
      retrieval_context: interaction.place.context,
      display_context: {
        has_open_promotion: Boolean(interaction.displayContext?.hasOpenPromotion),
        has_bogo_label: Boolean(interaction.displayContext?.hasBOGOLabel),
        urgency_signals: interaction.displayContext?.urgencySignals || [],
      },
      venue_context: {
        venue_id: interaction.venueId ?? null,
      },
    };
  }
}
